import React, { ReactNode, useEffect, useRef, useState } from "react";
import { Flex, Grid, Input, Text } from "@chakra-ui/react";
import { Formik } from "formik";
import AppSelect from "@/features/line-sheet-sets/app-select";
import AppButton from "@/features/line-sheet-sets/app-button";
import CustomApprovalModal from "@/shared/components/custom-modal";
import PathPickBadge from "@/features/pathpick/components/ui/PathPickBadge";
import PathPickInlineNotice from "@/features/pathpick/components/ui/PathPickInlineNotice";
import PathPickBasketBuyerInfoExampleModal from "@/features/pathpick/features/basket/components/buyer-info/guides/PathPickBasketBuyerInfoExampleModal";
import PathPickBasketDeliveryTemplateGuideModal from "@/features/pathpick/features/basket/components/buyer-info/guides/PathPickBasketDeliveryTemplateGuideModal";
import PathPickBasketOverseasReleaseGuideModal from "@/features/pathpick/features/basket/components/buyer-info/guides/PathPickBasketOverseasReleaseGuideModal";
import PathPickBasketApiSyncGuideModal from "@/features/pathpick/features/basket/components/buyer-info/guides/PathPickBasketApiSyncGuideModal";
import PathPickBasketBuyerInfoSection, {
  PATH_PICK_BUYER_INFO_INPUT_STYLE_PROPS,
  PathPickBasketBuyerInfoSectionGuideLink,
} from "@/features/pathpick/features/basket/components/buyer-info/PathPickBasketBuyerInfoSection";
import PathPickBasketTemplateImageField from "@/features/pathpick/features/basket/components/buyer-info/PathPickBasketTemplateImageField";
import PathPickBasketOverseasReleaseField from "@/features/pathpick/features/basket/components/buyer-info/PathPickBasketOverseasReleaseField";
import PathPickBasketNaverPartnerField from "@/features/pathpick/features/basket/components/buyer-info/PathPickBasketNaverPartnerField";
import PathPickBasketOverseasSalesPermissionNotice from "@/features/pathpick/features/basket/components/buyer-info/PathPickBasketOverseasSalesPermissionNotice";
import PathPickBasketSellingPriceField from "@/features/pathpick/features/basket/components/buyer-info/PathPickBasketSellingPriceField";
import PathPickPriceInput from "@/features/pathpick/components/ui/PathPickPriceInput";
import useGetSmartStoreInfo from "@/features/pathpick/features/basket/hooks/query/useGetSmartStoreInfo";
import useGetNaverPartnerAddressBooks from "@/features/pathpick/features/basket/hooks/query/useGetNaverPartnerAddressBooks";
import useCreateSmartStoreInfo from "@/features/pathpick/features/basket/hooks/mutation/useCreateSmartStoreInfo";
import useUpdateSmartStoreInfo from "@/features/pathpick/features/basket/hooks/mutation/useUpdateSmartStoreInfo";
import useUploadUnoFile from "@/hooks/use-upload-uno-file";
import useAppToasts from "@/features/line-sheet-sets/hooks/use-app-toasts";
import useMembership from "@/shared/hooks/useMembership";
import { findOverseasReleaseAddress } from "@/features/pathpick/features/basket/utils";
import { PathPickBuyerInfoVariant } from "@/features/pathpick/features/basket/contexts/syncFlowReducer";
import { PathPickSmartStoreInfoResponse } from "@/features/pathpick/features/basket/types/response";
import { PathPickSalePriceRuleType } from "@/features/pathpick/features/basket/types";
import { PATH_PICK_DELIVERY_COMPANIES } from "@/features/pathpick/features/basket/constants/deliveryCompanies";
import CustomIcon from "../../../../../../../public/icons-v1_1";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  // 어떤 진입 경로로 열렸는지 — 노출 섹션·필수 여부·배너 문구가 이 값으로 결정된다.
  variant: PathPickBuyerInfoVariant;
  // 저장했는데도 관문이 그대로일 때(예: 주소록에 해외 출고지가 없음) 배너를 경고로 바꾼다.
  isBlocked: boolean;
  // 저장 성공 — 호출부가 저장된 정보로 관문을 다시 판정해 신청을 이어가거나 모달을 닫는다.
  onSaved: (saved: PathPickSmartStoreInfoResponse) => void;
  // 저장으로 마지막 관문이 풀려 곧바로 신청이 접수되는 동안(파일 신청) 저장 버튼을 로딩으로 유지한다.
  isResumingRequest: boolean;
}

// 저장된 계산식이 없을 때 각 인상 방식의 입력에 미리 채워두는 값.
const DEFAULT_PRICE_MARKUP_RATE = 10;
const DEFAULT_PRICE_MARKUP_AMOUNT = 50000;

// 신청 도중 관문으로 열렸을 때 상단에 띄우는 배너 문구. standalone·가이드 경로는 배너가 없다.
const VARIANT_BANNER_KEYS: Record<PathPickBuyerInfoVariant, string | null> = {
  standalone: null,
  guideApi: null,
  guideExcel: null,
  apiSync: "path_pick:basket.buyer_info.banner_api_sync",
  fileRequest: "path_pick:basket.buyer_info.banner_file_request",
};

interface BuyerInfoFormValues {
  contact: string;
  notice: string;
  deliveryCompany?: string;
  deliveryTemplateCode: string;
  // 0원도 유효한 값이므로 미입력은 undefined로 구분한다.
  returnDeliveryFee?: number;
  exchangeDeliveryFee?: number;
  // 새로 선택한 파일 — null이면 서버에 저장된 기존 이미지를 유지한다.
  topImage: File | null;
  bottomImage: File | null;
  // 상품 등록 시 판매가 설정.
  // 비율과 금액은 단위가 달라 방식 전환 시 서로를 덮지 않도록 따로 들고 있는다.
  priceMarkupType: PathPickSalePriceRuleType;
  priceMarkupRate?: number;
  priceMarkupAmount?: number;
  isSalePriceSync: boolean;
}

// 시작 가이드에서 열린 경로 — 판매 방식만 다르고 배너·필수·저장 조건은 똑같이 다룬다.
function isGuideVariant(variant: PathPickBuyerInfoVariant) {
  return variant === "guideApi" || variant === "guideExcel";
}

// 저장 가능 여부 — 진입 경로가 정한다.
// standalone(설정 버튼)과 가이드 경로는 부분 저장을 허용하고, 신청 관문으로 열린
// 경우에는 그 경로에서 노출되는 필드가(상/하단 이미지 제외) 모두 채워져야 한다.
// 판매자ID·해외 출고지는 폼이 아니라 전용 API/주소록 조회가 판정하므로 저장 조건과 무관하다.
function canSave(
  values: BuyerInfoFormValues,
  variant: PathPickBuyerInfoVariant
) {
  if (variant === "standalone" || isGuideVariant(variant)) {
    return true;
  }

  const isAfterServiceFilled = Boolean(
    values.contact.trim() && values.notice.trim()
  );
  // 택배사·반품/교환 배송비는 별도 섹션으로 분리돼 모든 경로에서 노출되므로 공통 필수다.
  const isDeliveryFilled = Boolean(
    values.deliveryCompany &&
      // 배송비는 0원도 유효하므로 값의 유무 대신 숫자 여부로 판정한다.
      typeof values.returnDeliveryFee === "number" &&
      typeof values.exchangeDeliveryFee === "number"
  );

  // 템플릿 코드는 파일 신청 전용이라 API 연동 경로에서는 섹션 자체가 감춰진다 —
  // 감춘 값을 필수로 두면 저장이 영구 차단되므로 조건에서도 뺀다.
  if (variant === "apiSync") {
    return isAfterServiceFilled && isDeliveryFilled;
  }

  return (
    isAfterServiceFilled &&
    isDeliveryFilled &&
    Boolean(values.deliveryTemplateCode.trim())
  );
}

// 필드 라벨 — 필수인 경우에만 오른쪽에 붉은 * 를 붙인다.
const FieldLabel = ({
  children,
  isRequired,
}: {
  children: ReactNode;
  isRequired: boolean;
}) => (
  <Flex gap={"2px"} alignItems={"center"}>
    <Text textStyle={"body-sm-bold"} color={"#1D1D1B"}>
      {children}
    </Text>
    {isRequired && (
      <Text textStyle={"body-sm-bold"} color={"#EE7D7A"}>
        {"*"}
      </Text>
    )}
  </Flex>
);

function PathPickBasketBuyerInfoModal({
  isOpen,
  onClose,
  variant,
  isBlocked,
  onSaved,
  isResumingRequest,
}: Props) {
  const { t } = useI18nHelper();
  const { error: toastError } = useAppToasts({ id: "GLOBAL" });
  // 조회 실패 토스트는 저장 실패 토스트(GLOBAL)와 id를 나눈다 — 같은 id를 쓰면 서로를 덮어쓴다.
  // 전용 id를 두면 모달을 닫을 때 이 토스트만 자동으로 사라진다(useAppToasts의 언마운트 정리).
  const { error: toastLoadError } = useAppToasts({
    id: "PATH_PICK_BUYER_INFO_LOAD",
    withServerMessage: true,
  });
  // 해외 출고지·API 정보는 API 연동 전용이라 파일 신청 경로와 가이드 엑셀 흐름에서는 숨긴다.
  // 그 외 경로에서는 기존대로 연동 권한(프로 이상)에서만 노출한다.
  const { isApiSync } = useMembership();
  const showApiSyncSections =
    variant !== "fileRequest" && variant !== "guideExcel" && isApiSync;
  // 배송비 템플릿은 스마트스토어 파일 신청에만 필요하다 — API 연동 경로에서는 감춘다.
  const showDeliveryTemplateSection =
    variant !== "apiSync" && variant !== "guideApi";
  // 부분 저장을 허용하는 경로(standalone·가이드)에서는 필수 표시를 하지 않는다.
  const isRequired = variant === "apiSync" || variant === "fileRequest";

  const {
    smartStoreInfo,
    isError: isSmartStoreInfoError,
    error: smartStoreInfoError,
    errorUpdatedAt: smartStoreInfoErrorUpdatedAt,
    isFetching: isSmartStoreInfoFetching,
    refetch: refetchSmartStoreInfo,
  } = useGetSmartStoreInfo();
  // 저장 시 출고지로 지정할 주소를 주소록에서 찾기 위해 읽는다.
  const {
    addressBooks,
    error: addressBooksError,
    errorUpdatedAt: addressBooksErrorUpdatedAt,
    statusError: naverPartnerStatusError,
    statusErrorUpdatedAt,
  } = useGetNaverPartnerAddressBooks();
  const { mutateAsync: uploadFile } = useUploadUnoFile();
  const { mutate: createSmartStoreInfo, isPending: isCreating } =
    useCreateSmartStoreInfo();
  const { mutate: updateSmartStoreInfo, isPending: isUpdating } =
    useUpdateSmartStoreInfo();

  const [isExampleModalOpen, setIsExampleModalOpen] = useState<boolean>(false);
  const [isTemplateGuideOpen, setIsTemplateGuideOpen] =
    useState<boolean>(false);
  const [isOverseasGuideOpen, setIsOverseasGuideOpen] =
    useState<boolean>(false);
  const [isApiSyncGuideOpen, setIsApiSyncGuideOpen] = useState<boolean>(false);

  const isFilled = Boolean(smartStoreInfo);
  // 판매가 계산식은 구간별 배열로 저장되지만 편집 UI는 전 구간 규칙 하나만 다룬다.
  const savedSalePriceRule = smartStoreInfo?.salePriceRules?.[0];
  const isSaving = isCreating || isUpdating;
  const bannerKey = VARIANT_BANNER_KEYS[variant];

  // 저장 여부를 아예 모르는 상태 — 한 번도 못 읽었을 때만이다.
  // (다시 읽다 실패해도 앞서 받아둔 값이 있으면 그 값으로 수정 저장할 수 있다.
  //  404는 훅에서 null(미설정)로 흡수하므로 여기 걸리지 않는다)
  const isSmartStoreInfoUnavailable =
    isSmartStoreInfoError && smartStoreInfo === undefined;

  // 조회가 여럿 실패해도 토스트는 하나만 띄운다 — 폼 전체를 못 그리는 기본 정보가 가장 앞이고,
  // 나머지 원인은 각 섹션이 인라인으로 알린다.
  const loadFailure = [
    { error: smartStoreInfoError, at: smartStoreInfoErrorUpdatedAt },
    { error: naverPartnerStatusError, at: statusErrorUpdatedAt },
    { error: addressBooksError, at: addressBooksErrorUpdatedAt },
  ].find(({ error }) => Boolean(error));
  const loadError = loadFailure?.error;
  const loadErrorUpdatedAt = loadFailure?.at ?? 0;

  // 실패한 쿼리의 error는 캐시에 남아 모달을 다시 열어도 그대로 읽힌다 —
  // 연결 상태를 몰라 주소록 조회가 비활성이면 요청이 다시 나가지 않아 그 값을 지울 기회조차 없다.
  // 그래서 "에러가 있음"이 아니라 "이번에 새로 실패했음"을 기준으로 알린다.
  // (마운트마다 초기화되는 ref로 중복을 막으면, 모달을 다시 열 때마다 지난 실패가 되살아난다)
  const mountedAtRef = useRef<number>(Date.now());
  const notifiedAtRef = useRef<number>(0);
  useEffect(() => {
    if (!loadError) {
      return;
    }
    // 모달을 열기 전에 확정된 에러는 지난번 실패의 잔상이다 — 인라인 안내가 이미 알리고 있다.
    if (loadErrorUpdatedAt <= mountedAtRef.current) {
      return;
    }
    // 같은 실패를 리렌더마다 다시 알리지 않는다.
    if (loadErrorUpdatedAt === notifiedAtRef.current) {
      return;
    }
    notifiedAtRef.current = loadErrorUpdatedAt;
    // 안내 문구는 common:message.error.unknown 한 벌로 통일하고, 어느 조회가 왜 막혔는지는
    // withServerMessage로 켜 둔 보조 줄이 알린다(각 섹션의 인라인 안내는 그대로 남는다).
    toastLoadError(loadError);
  }, [loadError, loadErrorUpdatedAt, toastLoadError]);

  const deliveryCompanyOptions = PATH_PICK_DELIVERY_COMPANIES.map(
    (company) => ({
      name: company.name,
      value: company.code,
    })
  );

  const handleSubmit = async (values: BuyerInfoFormValues) => {
    // 저장된 정보를 못 읽은 상태에서 저장하면 등록/수정 분기가 뒤집혀(POST) 폼에 없는 값
    // — 출고지·반품지·상/하단 이미지 — 이 빈 값으로 덮인다. 읽기 전에는 쓰지 않는다.
    if (!canSave(values, variant) || isSmartStoreInfoUnavailable) {
      return;
    }

    // 1) 새로 선택된 이미지를 먼저 업로드해 file id를 확보한다 (미선택이면 기존 id 유지).
    let topImageFileId: number | undefined;
    let bottomImageFileId: number | undefined;
    try {
      [topImageFileId, bottomImageFileId] = await Promise.all([
        values.topImage
          ? uploadFile(values.topImage).then((res) => res.data.id)
          : smartStoreInfo?.topImage?.id,
        values.bottomImage
          ? uploadFile(values.bottomImage).then((res) => res.data.id)
          : smartStoreInfo?.bottomImage?.id,
      ]);
    } catch (err) {
      toastError(err);
      return;
    }

    // 2) 판매가 계산식 — 고른 방식의 값만 싣는다. 값이 비어 있으면 계산식 없음(null)이고,
    //    그때는 공급가가 그대로 판매가가 된다(천원 단위 올림은 서버가 항상 적용).
    //    구간 편집 UI가 없어 전 구간(gt·lte 없음)을 덮는 규칙 하나로 보낸다.
    const markupValue =
      values.priceMarkupType === "PERCENT"
        ? values.priceMarkupRate
        : values.priceMarkupAmount;

    // 3) 저장된 정보가 있으면 수정(PUT), 없으면 등록(POST). 실패 토스트는 훅의 onError가 담당.
    //    standalone은 부분 저장을 허용하므로 미입력은 null로 보낸다.
    const save = isFilled ? updateSmartStoreInfo : createSmartStoreInfo;
    save(
      {
        afterServiceTelephoneNumber: values.contact.trim() || null,
        afterServiceGuideContent: values.notice.trim() || null,
        // 배송방법 선택 UI가 없어졌다 — 택배사를 고르는 화면이므로 택배로 고정한다.
        deliveryType: "DELIVERY",
        deliveryCompany: values.deliveryCompany ?? null,
        deliveryTemplateCode: values.deliveryTemplateCode.trim() || null,
        returnDeliveryFee: values.returnDeliveryFee ?? null,
        exchangeDeliveryFee: values.exchangeDeliveryFee ?? null,
        salePriceRules:
          typeof markupValue === "number"
            ? [
                {
                  range: { gt: null, lte: null },
                  type: values.priceMarkupType,
                  value: markupValue,
                },
              ]
            : null,
        isSalePriceSync: values.isSalePriceSync,
        // 확인된 해외 출고지가 있으면 출고지로 지정하고, 없으면 저장된 설정을 그대로 유지한다.
        releaseAddressId:
          findOverseasReleaseAddress(addressBooks)?.addressBookNo ??
          smartStoreInfo?.releaseAddressId ??
          undefined,
        // 반품/교환지는 선택 UI가 없어 저장된 값을 그대로 다시 보낸다.
        refundAddressId: smartStoreInfo?.refundAddressId ?? undefined,
        topImageFileId,
        bottomImageFileId,
      },
      { onSuccess: (saved) => onSaved(saved) }
    );
  };

  return (
    <Formik<BuyerInfoFormValues>
      enableReinitialize
      initialValues={{
        contact: smartStoreInfo?.afterServiceTelephoneNumber ?? "",
        notice: smartStoreInfo?.afterServiceGuideContent ?? "",
        // 서버가 미설정 필드를 null로 주므로 폼 값 규약(미입력=undefined)에 맞춰 정규화한다.
        deliveryCompany: smartStoreInfo?.deliveryCompany ?? undefined,
        deliveryTemplateCode: smartStoreInfo?.deliveryTemplateCode ?? "",
        returnDeliveryFee: smartStoreInfo?.returnDeliveryFee ?? undefined,
        exchangeDeliveryFee: smartStoreInfo?.exchangeDeliveryFee ?? undefined,
        topImage: null,
        bottomImage: null,
        // 구간 편집 UI가 없어 전 구간을 덮는 규칙 하나만 다룬다 — 저장된 규칙이 여럿이면 첫 규칙을 읽는다.
        priceMarkupType: savedSalePriceRule?.type ?? "PERCENT",
        // 고른 방식과 다른 쪽 칸은 저장값으로 채우지 않는다 — %로 저장된 10을 원 칸에 넣으면 10원이 된다.
        priceMarkupRate:
          savedSalePriceRule?.type === "PERCENT"
            ? savedSalePriceRule.value
            : DEFAULT_PRICE_MARKUP_RATE,
        priceMarkupAmount:
          savedSalePriceRule?.type === "AMOUNT"
            ? savedSalePriceRule.value
            : DEFAULT_PRICE_MARKUP_AMOUNT,
        isSalePriceSync: smartStoreInfo?.isSalePriceSync ?? false,
      }}
      onSubmit={handleSubmit}
    >
      {({
        values,
        handleChange,
        setFieldValue,
        handleSubmit,
        isSubmitting,
      }) => (
        <CustomApprovalModal
          isOpen={isOpen}
          onClose={onClose}
          // 화면보다 넓으면 왼쪽이 스크롤로도 닿지 않아 화면 폭으로 클램프한다.
          minWidth={"min(720px, 92dvw)"}
          scrollBehavior={"inside"}
          isCentered={true}
        >
          <CustomApprovalModal.Header
            onClose={onClose}
            title={
              <Flex gap={"8px"} alignItems={"center"}>
                <CustomIcon
                  src={"user"}
                  width={20}
                  height={20}
                  color={"#1272EF"}
                />
                <Flex flexDirection={"column"} gap={"2px"}>
                  <Flex gap={"6px"} alignItems={"center"}>
                    <Text textStyle={"body-md-bold"} color={"#444444"}>
                      {t("path_pick:basket.buyer_info.modal_title")}
                    </Text>
                    {/* 조회에 실패했으면 저장 여부를 단정할 수 없다 — "미저장"으로 보이면 안 된다. */}
                    <PathPickBadge
                      label={t(
                        isSmartStoreInfoUnavailable
                          ? "path_pick:basket.buyer_info.status_unknown"
                          : isFilled
                          ? "path_pick:basket.buyer_info.status_saved"
                          : "path_pick:basket.buyer_info.status_unsaved"
                      )}
                      colorVariant={
                        isSmartStoreInfoUnavailable
                          ? "red"
                          : isFilled
                          ? "green"
                          : "gray"
                      }
                    />
                  </Flex>
                  {/* 좁은 폭에서는 헤더가 본문을 밀어내지 않게 설명을 감춘다. */}
                  <Text
                    textStyle={"body-sm-regular"}
                    color={"#8F8F8C"}
                    display={{ base: "none", md: "block" }}
                  >
                    {t("path_pick:basket.buyer_info.modal_description")}
                  </Text>
                </Flex>
              </Flex>
            }
          />
          <CustomApprovalModal.Body>
            <Flex flexDirection={"column"} gap={"12px"}>
              {/* 해외상품판매 권한은 다른 설정보다 앞서야 하는 전제 조건이라 본문 맨 위에 둔다.
                  판매 방식·진입 경로와 무관하게 항상 노출한다. */}
              <PathPickBasketOverseasSalesPermissionNotice />

              {/* 신청 도중 관문으로 열렸을 때만 — 왜 떴는지, 저장하면 어떻게 되는지 알린다.
                  저장했는데도 관문이 남아 있으면(isBlocked) 같은 자리에서 경고로 바뀐다. */}
              {bannerKey && (
                <Flex
                  flexDirection={"column"}
                  gap={"4px"}
                  padding={"12px 16px"}
                  borderRadius={"8px"}
                  border={
                    isBlocked ? "0.5px solid #EE7D7A" : "0.5px solid #1272EF"
                  }
                  background={isBlocked ? "#FFEEEE" : "#F0F6FF"}
                >
                  <Text
                    textStyle={"body-sm-bold"}
                    color={isBlocked ? "#CC1D1D" : "#1272EF"}
                  >
                    {t(bannerKey)}
                  </Text>
                  {isBlocked && (
                    <Text textStyle={"body-sm-medium"} color={"#CC1D1D"}>
                      {t("path_pick:basket.buyer_info.reason_still_blocked")}
                    </Text>
                  )}
                </Flex>
              )}

              {/* 저장된 기본 정보를 못 읽었을 때 — 저장 버튼이 잠기는 이유를 먼저 알린다. */}
              {isSmartStoreInfoUnavailable && (
                <PathPickInlineNotice
                  tone={"red"}
                  title={t("path_pick:basket.buyer_info.load_error.title")}
                  action={
                    <AppButton
                      variant={"white"}
                      size={"small"}
                      isLoading={isSmartStoreInfoFetching}
                      onClick={() => refetchSmartStoreInfo()}
                    >
                      <Text textStyle={"body-sm-bold"}>
                        {t("path_pick:basket.buyer_info.load_error.retry")}
                      </Text>
                    </AppButton>
                  }
                >
                  {t("path_pick:basket.buyer_info.load_error.description")}
                </PathPickInlineNotice>
              )}

              {/* 스마트스토어 API 정보 — API 연동 전용.
                  연동의 출발점이라 입력 섹션 중 가장 앞에 둔다.
                  이 섹션만 저장 버튼이 아니라 전용 연결/해제 API로 즉시 반영된다. */}
              {showApiSyncSections && (
                <PathPickBasketBuyerInfoSection
                  icon={"link"}
                  title={t("path_pick:basket.buyer_info.api_info.title")}
                  description={t(
                    "path_pick:basket.buyer_info.api_info.description"
                  )}
                  action={
                    <PathPickBasketBuyerInfoSectionGuideLink
                      label={t("path_pick:basket.buyer_info.api_guide.button")}
                      onClick={() => setIsApiSyncGuideOpen(true)}
                    />
                  }
                >
                  <Flex flexDirection={"column"} gap={"6px"}>
                    <FieldLabel isRequired={isRequired}>
                      {t(
                        "path_pick:basket.buyer_info.api_info.seller_id_label"
                      )}
                    </FieldLabel>
                    <PathPickBasketNaverPartnerField />
                  </Flex>
                </PathPickBasketBuyerInfoSection>
              )}

              {/* 상품 등록 시 판매가 설정 — 파일 신청·API 연동 어느 경로로 등록하든 적용되는
                  설정이라 진입 경로와 무관하게 노출한다. */}
              <PathPickBasketBuyerInfoSection
                icon={"coin"}
                title={t("path_pick:basket.buyer_info.selling_price.title")}
                description={t(
                  "path_pick:basket.buyer_info.selling_price.description"
                )}
              >
                <PathPickBasketSellingPriceField
                  markupType={values.priceMarkupType}
                  markupRate={values.priceMarkupRate}
                  markupAmount={values.priceMarkupAmount}
                  isSalePriceSync={values.isSalePriceSync}
                  onChangeMarkupType={(markupType) =>
                    setFieldValue("priceMarkupType", markupType)
                  }
                  onChangeMarkupRate={(rate) =>
                    setFieldValue("priceMarkupRate", rate)
                  }
                  onChangeMarkupAmount={(amount) =>
                    setFieldValue("priceMarkupAmount", amount)
                  }
                  onChangeSalePriceSync={(isSalePriceSync) =>
                    setFieldValue("isSalePriceSync", isSalePriceSync)
                  }
                />
              </PathPickBasketBuyerInfoSection>

              {/* A/S 정보 */}
              <PathPickBasketBuyerInfoSection
                icon={"user"}
                title={t("path_pick:basket.buyer_info.after_service.title")}
                description={t(
                  "path_pick:basket.buyer_info.after_service.description"
                )}
              >
                {/* 모달이 화면 폭으로 줄어드는 모바일에서는 두 칸을 세로로 쌓는다(아래 Grid 모두 동일). */}
                <Grid
                  templateColumns={{ base: "1fr", md: "2fr 3fr" }}
                  gap={"16px"}
                  alignItems={"start"}
                >
                  <Flex flexDirection={"column"} gap={"6px"}>
                    <FieldLabel isRequired={isRequired}>
                      {t("path_pick:basket.buyer_info.contact_label")}
                    </FieldLabel>
                    <Input
                      name={"contact"}
                      value={values.contact}
                      onChange={handleChange}
                      placeholder={t(
                        "path_pick:basket.buyer_info.contact_placeholder"
                      )}
                      {...PATH_PICK_BUYER_INFO_INPUT_STYLE_PROPS}
                    />
                  </Flex>
                  <Flex flexDirection={"column"} gap={"6px"}>
                    <FieldLabel isRequired={isRequired}>
                      {t("path_pick:basket.buyer_info.notice_label")}
                    </FieldLabel>
                    <Input
                      name={"notice"}
                      value={values.notice}
                      onChange={handleChange}
                      placeholder={t(
                        "path_pick:basket.buyer_info.notice_placeholder"
                      )}
                      {...PATH_PICK_BUYER_INFO_INPUT_STYLE_PROPS}
                    />
                  </Flex>
                </Grid>
              </PathPickBasketBuyerInfoSection>

              {/* 스마트스토어 배송비 템플릿 설정 — 파일 신청 전용이라 API 연동 경로에서는 빠진다 */}
              {showDeliveryTemplateSection && (
                <PathPickBasketBuyerInfoSection
                  icon={"map-pin"}
                  title={t(
                    "path_pick:basket.buyer_info.delivery_template.title"
                  )}
                  description={t(
                    "path_pick:basket.buyer_info.delivery_template.description"
                  )}
                  action={
                    <PathPickBasketBuyerInfoSectionGuideLink
                      label={t(
                        "path_pick:basket.buyer_info.template_code_guide.button"
                      )}
                      onClick={() => setIsTemplateGuideOpen(true)}
                    />
                  }
                >
                  <Flex flexDirection={"column"} gap={"6px"}>
                    <FieldLabel isRequired={isRequired}>
                      {t("path_pick:basket.buyer_info.delivery_template_code")}
                    </FieldLabel>
                    <Input
                      name={"deliveryTemplateCode"}
                      value={values.deliveryTemplateCode}
                      onChange={handleChange}
                      placeholder={t(
                        "path_pick:basket.buyer_info.delivery_template_code_placeholder"
                      )}
                      {...PATH_PICK_BUYER_INFO_INPUT_STYLE_PROPS}
                    />
                  </Flex>
                </PathPickBasketBuyerInfoSection>
              )}

              {/* 배송 정보 — 택배사·반품/교환 배송비는 파일 신청과 API 연동 양쪽에 모두 필요해서
                  템플릿 코드(파일 전용)와 분리해 모든 경로에서 노출한다. */}
              <PathPickBasketBuyerInfoSection
                icon={"truck"}
                title={t("path_pick:basket.buyer_info.delivery_info.title")}
                description={t(
                  "path_pick:basket.buyer_info.delivery_info.description"
                )}
              >
                <Flex flexDirection={"column"} gap={"6px"}>
                  <FieldLabel isRequired={isRequired}>
                    {t("path_pick:basket.buyer_info.delivery_company_label")}
                  </FieldLabel>
                  <AppSelect<string>
                    name={t(
                      "path_pick:basket.buyer_info.delivery_company_placeholder"
                    )}
                    value={values.deliveryCompany}
                    options={deliveryCompanyOptions}
                    onSelect={(deliveryCompany) => {
                      if (deliveryCompany) {
                        setFieldValue("deliveryCompany", deliveryCompany);
                      }
                    }}
                    // width를 지정하면 AppSelect의 matchWidth(트리거-드롭다운 너비 일치)가 꺼지므로 minWidth로 채운다.
                    minWidth={"100%"}
                    dropdownWidth={"100%"}
                    isSearchable={true}
                    isClearable={false}
                    height={"32px"}
                  />
                </Flex>
                <Grid
                  templateColumns={{
                    base: "1fr",
                    md: "repeat(2, minmax(0, 1fr))",
                  }}
                  gap={"16px"}
                >
                  <Flex flexDirection={"column"} gap={"6px"}>
                    <FieldLabel isRequired={isRequired}>
                      {t("path_pick:basket.buyer_info.return_delivery_fee")}
                    </FieldLabel>
                    <PathPickPriceInput
                      value={values.returnDeliveryFee}
                      onChange={(fee) =>
                        setFieldValue("returnDeliveryFee", fee)
                      }
                      suffix={t("path_pick:basket.buyer_info.won")}
                    />
                  </Flex>
                  <Flex flexDirection={"column"} gap={"6px"}>
                    <FieldLabel isRequired={isRequired}>
                      {t("path_pick:basket.buyer_info.exchange_delivery_fee")}
                    </FieldLabel>
                    <PathPickPriceInput
                      value={values.exchangeDeliveryFee}
                      onChange={(fee) =>
                        setFieldValue("exchangeDeliveryFee", fee)
                      }
                      suffix={t("path_pick:basket.buyer_info.won")}
                    />
                  </Flex>
                </Grid>
                <Flex
                  paddingY={"8px"}
                  paddingX={"10px"}
                  borderRadius={"8px"}
                  background={"#F7F9FA"}
                >
                  <Text textStyle={"body-sm-medium"} color={"#8F8F8C"}>
                    {t("path_pick:basket.buyer_info.refund_address_note")}
                  </Text>
                </Flex>
              </PathPickBasketBuyerInfoSection>

              {/* 해외 출고지 설정 — API 연동 전용 */}
              {showApiSyncSections && (
                <PathPickBasketBuyerInfoSection
                  icon={"map-pin"}
                  title={t(
                    "path_pick:basket.buyer_info.overseas_release.title"
                  )}
                  description={t(
                    "path_pick:basket.buyer_info.overseas_release.description"
                  )}
                  action={
                    <PathPickBasketBuyerInfoSectionGuideLink
                      label={t(
                        "path_pick:basket.overseas_release.guide_button"
                      )}
                      onClick={() => setIsOverseasGuideOpen(true)}
                    />
                  }
                >
                  <PathPickBasketOverseasReleaseField />
                </PathPickBasketBuyerInfoSection>
              )}

              {/* 상품 상세페이지 상/하단 업체별 이미지 — 유일하게 필수가 아닌 섹션 */}
              <PathPickBasketBuyerInfoSection
                icon={"document-text"}
                title={t("path_pick:basket.buyer_info.detail_images_title")}
                description={t(
                  "path_pick:basket.buyer_info.detail_images_description"
                )}
                action={
                  <PathPickBasketBuyerInfoSectionGuideLink
                    label={t("path_pick:basket.buyer_info.view_example")}
                    onClick={() => setIsExampleModalOpen(true)}
                  />
                }
              >
                <Grid
                  templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }}
                  gap={"12px"}
                >
                  <PathPickBasketTemplateImageField
                    title={t("path_pick:basket.buyer_info.top_image")}
                    file={values.topImage}
                    noImageText={t("path_pick:basket.form_modal.no_image")}
                    existingImage={smartStoreInfo?.topImage}
                    existingFileName={smartStoreInfo?.topImage?.name}
                    existingFileUrl={smartStoreInfo?.topImage?.url}
                    downloadText={t("common:download")}
                    onChangeFile={(file) => setFieldValue("topImage", file)}
                  />
                  <PathPickBasketTemplateImageField
                    title={t("path_pick:basket.buyer_info.bottom_image")}
                    file={values.bottomImage}
                    noImageText={t("path_pick:basket.form_modal.no_image")}
                    existingImage={smartStoreInfo?.bottomImage}
                    existingFileName={smartStoreInfo?.bottomImage?.name}
                    existingFileUrl={smartStoreInfo?.bottomImage?.url}
                    downloadText={t("common:download")}
                    onChangeFile={(file) => setFieldValue("bottomImage", file)}
                  />
                </Grid>
              </PathPickBasketBuyerInfoSection>
            </Flex>
          </CustomApprovalModal.Body>
          <CustomApprovalModal.Footer
            cancelName={t("common:cancel")}
            confirmName={t("path_pick:basket.buyer_info.save")}
            onCancel={onClose}
            onConfirm={() => handleSubmit()}
            isLoading={isSubmitting || isSaving || isResumingRequest}
            isConfirmDisabled={
              !canSave(values, variant) || isSmartStoreInfoUnavailable
            }
            confirmBtnProps={{
              width: "180px",
              leftIcon: (
                <CustomIcon
                  src={"user"}
                  width={16}
                  height={16}
                  color={"#FFFFFF"}
                />
              ),
            }}
          />
          {isExampleModalOpen && (
            <PathPickBasketBuyerInfoExampleModal
              isOpen
              onClose={() => setIsExampleModalOpen(false)}
            />
          )}
          {isTemplateGuideOpen && (
            <PathPickBasketDeliveryTemplateGuideModal
              isOpen
              onClose={() => setIsTemplateGuideOpen(false)}
            />
          )}
          {isOverseasGuideOpen && (
            <PathPickBasketOverseasReleaseGuideModal
              isOpen
              onClose={() => setIsOverseasGuideOpen(false)}
            />
          )}
          {isApiSyncGuideOpen && (
            <PathPickBasketApiSyncGuideModal
              isOpen
              onClose={() => setIsApiSyncGuideOpen(false)}
            />
          )}
        </CustomApprovalModal>
      )}
    </Formik>
  );
}

export default PathPickBasketBuyerInfoModal;
